import { lazy } from 'react';
import { Link, Route, Routes } from 'react-router';
import { AdminIndex, AdminLayout, AdminSection } from '../features/admin/AdminLayout';
import { ForgotPasswordPage } from '../features/auth/ForgotPasswordPage';
import { LoginPage } from '../features/auth/LoginPage';
import { MagicLinkPage } from '../features/auth/MagicLinkPage';
import { ResetPasswordPage } from '../features/auth/ResetPasswordPage';
import { CabinetPage } from '../features/cabinet/CabinetPage';
import { FounderDetailPage } from '../features/founders/FounderDetailPage';
import { FoundersPage } from '../features/founders/FoundersPage';
import { InvitePage } from '../features/invitations/InvitePage';
import { LegacyInviteRedirect } from '../features/invitations/LegacyInviteRedirect';
import { ProfilePage } from '../features/profile/ProfilePage';
import { ReauthProvider } from '../features/reauth/ReauthProvider';
import { SecurityPage } from '../features/security/SecurityPage';
import { AuthenticatedLayout } from './AuthenticatedLayout';
import { SessionBridge } from './SessionBridge';
import { HomeRedirect, WorkspaceLayout } from './WorkspaceLayout';
import { phrase } from '../lib/i18n/runtime';

// Administration is loaded on demand: most founders never open it.
const AuditLogAdmin = lazy(() =>
  import('../features/audit/AuditLogAdmin').then((m) => ({ default: m.AuditLogAdmin })),
);
const AiActivityAdmin = lazy(() =>
  import('../features/assistant/AiActivityAdmin').then((m) => ({ default: m.AiActivityAdmin })),
);
const CapTableAdmin = lazy(() =>
  import('../features/equity/CapTableAdmin').then((m) => ({ default: m.CapTableAdmin })),
);
const FoundersAdmin = lazy(() =>
  import('../features/founders/FoundersAdmin').then((m) => ({ default: m.FoundersAdmin })),
);
const InvitationsAdmin = lazy(() =>
  import('../features/invitations/InvitationsAdmin').then((m) => ({
    default: m.InvitationsAdmin,
  })),
);
const MembersAdmin = lazy(() =>
  import('../features/members/MembersAdmin').then((m) => ({ default: m.MembersAdmin })),
);
const PermissionGrantsAdmin = lazy(() =>
  import('../features/permission-grants/PermissionGrantsAdmin').then((m) => ({
    default: m.PermissionGrantsAdmin,
  })),
);
const SecuritySettingsAdmin = lazy(() =>
  import('../features/security-settings/SecuritySettingsAdmin').then((m) => ({
    default: m.SecuritySettingsAdmin,
  })),
);
const SystemStatus = lazy(() =>
  import('../features/system-status/SystemStatus').then((m) => ({ default: m.SystemStatus })),
);
const GovernanceAdmin = lazy(() =>
  import('../features/governance/GovernanceAdmin').then((m) => ({ default: m.GovernanceAdmin })),
);

// Governance pages are split out so the main bundle stays small.
const TopicsPage = lazy(() =>
  import('../features/topics/TopicsPage').then((m) => ({ default: m.TopicsPage })),
);
const NewTopicPage = lazy(() =>
  import('../features/topics/NewTopicPage').then((m) => ({ default: m.NewTopicPage })),
);
const TopicDetailPage = lazy(() =>
  import('../features/topics/TopicDetailPage').then((m) => ({ default: m.TopicDetailPage })),
);
const DecisionsPage = lazy(() =>
  import('../features/decisions/DecisionsPage').then((m) => ({ default: m.DecisionsPage })),
);
const NewDecisionPage = lazy(() =>
  import('../features/decisions/NewDecisionPage').then((m) => ({ default: m.NewDecisionPage })),
);
const DecisionDetailPage = lazy(() =>
  import('../features/decisions/DecisionDetailPage').then((m) => ({
    default: m.DecisionDetailPage,
  })),
);
const NotificationsPage = lazy(() =>
  import('../features/notifications/NotificationsPage').then((m) => ({
    default: m.NotificationsPage,
  })),
);

// Execution: tasks, blockers, weekly updates and the execution dashboard.
const TasksPage = lazy(() =>
  import('../features/tasks/TasksPage').then((m) => ({ default: m.TasksPage })),
);
const NewTaskPage = lazy(() =>
  import('../features/tasks/NewTaskPage').then((m) => ({ default: m.NewTaskPage })),
);
const TaskDetailPage = lazy(() =>
  import('../features/tasks/TaskDetailPage').then((m) => ({ default: m.TaskDetailPage })),
);
const BlockersPage = lazy(() =>
  import('../features/tasks/BlockersPage').then((m) => ({ default: m.BlockersPage })),
);
const UpdatesPage = lazy(() =>
  import('../features/updates/UpdatesPage').then((m) => ({ default: m.UpdatesPage })),
);
const TeamUpdatesPage = lazy(() =>
  import('../features/updates/TeamUpdatesPage').then((m) => ({ default: m.TeamUpdatesPage })),
);
const PeriodPage = lazy(() =>
  import('../features/updates/PeriodPage').then((m) => ({ default: m.PeriodPage })),
);
const UpdateDetailPage = lazy(() =>
  import('../features/updates/UpdateDetailPage').then((m) => ({ default: m.UpdateDetailPage })),
);
const ExecutionDashboardPage = lazy(() =>
  import('../features/reports/ExecutionDashboardPage').then((m) => ({
    default: m.ExecutionDashboardPage,
  })),
);
const MasterDashboardPage = lazy(() =>
  import('../features/reports/MasterDashboardPage').then((m) => ({
    default: m.MasterDashboardPage,
  })),
);
const ExecutionSettingsAdmin = lazy(() =>
  import('../features/execution-settings/ExecutionSettingsAdmin').then((m) => ({
    default: m.ExecutionSettingsAdmin,
  })),
);

// Meetings, goals, search and notification settings.
const MeetingsPage = lazy(() =>
  import('../features/meetings/MeetingsPage').then((m) => ({ default: m.MeetingsPage })),
);
const NewMeetingPage = lazy(() =>
  import('../features/meetings/NewMeetingPage').then((m) => ({ default: m.NewMeetingPage })),
);
const MeetingDetailPage = lazy(() =>
  import('../features/meetings/MeetingDetailPage').then((m) => ({
    default: m.MeetingDetailPage,
  })),
);
const GoalsPage = lazy(() =>
  import('../features/goals/GoalsPage').then((m) => ({ default: m.GoalsPage })),
);
const NewGoalPage = lazy(() =>
  import('../features/goals/NewGoalPage').then((m) => ({ default: m.NewGoalPage })),
);
const GoalDetailPage = lazy(() =>
  import('../features/goals/GoalDetailPage').then((m) => ({ default: m.GoalDetailPage })),
);
const SearchPage = lazy(() =>
  import('../features/search/SearchPage').then((m) => ({ default: m.SearchPage })),
);
const NotificationSettingsPage = lazy(() =>
  import('../features/notifications/NotificationSettingsPage').then((m) => ({
    default: m.NotificationSettingsPage,
  })),
);
const NotificationPolicyAdmin = lazy(() =>
  import('../features/notifications/NotificationPolicyAdmin').then((m) => ({
    default: m.NotificationPolicyAdmin,
  })),
);

// Company finance.
const FinanceLayout = lazy(() =>
  import('../features/finance/FinanceLayout').then((m) => ({ default: m.FinanceLayout })),
);
const FinanceOverviewPage = lazy(() =>
  import('../features/finance/FinanceOverviewPage').then((m) => ({
    default: m.FinanceOverviewPage,
  })),
);
const TransactionsPage = lazy(() =>
  import('../features/finance/TransactionsPage').then((m) => ({ default: m.TransactionsPage })),
);
const NewTransactionPage = lazy(() =>
  import('../features/finance/NewTransactionPage').then((m) => ({
    default: m.NewTransactionPage,
  })),
);
const TransactionDetailPage = lazy(() =>
  import('../features/finance/TransactionDetailPage').then((m) => ({
    default: m.TransactionDetailPage,
  })),
);
const PnlPage = lazy(() =>
  import('../features/finance/PnlPage').then((m) => ({ default: m.PnlPage })),
);
const CashPage = lazy(() =>
  import('../features/finance/CashPage').then((m) => ({ default: m.CashPage })),
);
const ReconciliationPage = lazy(() =>
  import('../features/finance/ReconciliationPage').then((m) => ({
    default: m.ReconciliationPage,
  })),
);
const FundingOverviewPage = lazy(() =>
  import('../features/funding/FundingOverviewPage').then((m) => ({
    default: m.FundingOverviewPage,
  })),
);
const NewFundingCallPage = lazy(() =>
  import('../features/funding/NewFundingCallPage').then((m) => ({
    default: m.NewFundingCallPage,
  })),
);
const FundingCallPage = lazy(() =>
  import('../features/funding/FundingCallPage').then((m) => ({ default: m.FundingCallPage })),
);
const ContributionPage = lazy(() =>
  import('../features/funding/ContributionPage').then((m) => ({ default: m.ContributionPage })),
);
const DebtPage = lazy(() =>
  import('../features/funding/DebtPage').then((m) => ({ default: m.DebtPage })),
);
const FounderFinancePage = lazy(() =>
  import('../features/funding/FounderFinancePage').then((m) => ({
    default: m.FounderFinancePage,
  })),
);
const FinanceAdmin = lazy(() =>
  import('../features/finance/FinanceAdmin').then((m) => ({ default: m.FinanceAdmin })),
);
const DistributionsOverviewPage = lazy(() =>
  import('../features/distributions/DistributionsOverviewPage').then((m) => ({
    default: m.DistributionsOverviewPage,
  })),
);
const NewDistributionPage = lazy(() =>
  import('../features/distributions/NewDistributionPage').then((m) => ({
    default: m.NewDistributionPage,
  })),
);
const DistributionPage = lazy(() =>
  import('../features/distributions/DistributionPage').then((m) => ({
    default: m.DistributionPage,
  })),
);
const ScenarioPage = lazy(() =>
  import('../features/distributions/ScenarioPage').then((m) => ({ default: m.ScenarioPage })),
);
const ReservePage = lazy(() =>
  import('../features/distributions/ReservePage').then((m) => ({ default: m.ReservePage })),
);
const ValuationOverviewPage = lazy(() =>
  import('../features/valuation/ValuationOverviewPage').then((m) => ({
    default: m.ValuationOverviewPage,
  })),
);
const NewValuationPage = lazy(() =>
  import('../features/valuation/NewValuationPage').then((m) => ({
    default: m.NewValuationPage,
  })),
);
const ValuationSnapshotPage = lazy(() =>
  import('../features/valuation/ValuationSnapshotPage').then((m) => ({
    default: m.ValuationSnapshotPage,
  })),
);
const RecalculateValuationPage = lazy(() =>
  import('../features/valuation/RecalculateValuationPage').then((m) => ({
    default: m.RecalculateValuationPage,
  })),
);
const ComparablesPage = lazy(() =>
  import('../features/valuation/ComparablesPage').then((m) => ({ default: m.ComparablesPage })),
);

export function AppRoutes() {
  return (
    <ReauthProvider>
      <SessionBridge />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/magic-link" element={<MagicLinkPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/invite" element={<InvitePage />} />
        <Route path="/invitations/accept" element={<LegacyInviteRedirect />} />
        <Route element={<AuthenticatedLayout />}>
          <Route index element={<HomeRedirect />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="w/:workspaceId" element={<WorkspaceLayout />}>
            <Route index element={<CabinetPage />} />
            <Route path="founders" element={<FoundersPage />} />
            <Route path="founders/:founderId" element={<FounderDetailPage />} />
            <Route path="topics" element={<TopicsPage />} />
            <Route path="topics/new" element={<NewTopicPage />} />
            <Route path="topics/:topicId" element={<TopicDetailPage />} />
            <Route path="decisions" element={<DecisionsPage />} />
            <Route path="decisions/new" element={<NewDecisionPage />} />
            <Route path="decisions/:decisionId" element={<DecisionDetailPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="tasks/new" element={<NewTaskPage />} />
            <Route path="tasks/:taskId" element={<TaskDetailPage />} />
            <Route path="blockers" element={<BlockersPage />} />
            <Route path="updates" element={<UpdatesPage />} />
            <Route path="updates/team" element={<TeamUpdatesPage />} />
            <Route path="updates/periods/:periodId" element={<PeriodPage />} />
            <Route path="updates/u/:updateId" element={<UpdateDetailPage />} />
            <Route path="execution" element={<ExecutionDashboardPage />} />
            <Route path="reports" element={<MasterDashboardPage />} />
            <Route path="meetings" element={<MeetingsPage />} />
            <Route path="meetings/new" element={<NewMeetingPage />} />
            <Route path="meetings/:meetingId" element={<MeetingDetailPage />} />
            <Route path="goals" element={<GoalsPage />} />
            <Route path="goals/new" element={<NewGoalPage />} />
            <Route path="goals/:goalId" element={<GoalDetailPage />} />
            <Route path="finance" element={<FinanceLayout />}>
              <Route index element={<FinanceOverviewPage />} />
              <Route path="transactions" element={<TransactionsPage />} />
              <Route path="transactions/new" element={<NewTransactionPage />} />
              <Route path="transactions/:transactionId" element={<TransactionDetailPage />} />
              <Route path="pnl" element={<PnlPage />} />
              <Route path="cash" element={<CashPage />} />
              <Route path="reconciliation" element={<ReconciliationPage />} />
              <Route path="funding" element={<FundingOverviewPage />} />
              <Route path="funding/new" element={<NewFundingCallPage />} />
              <Route path="funding/calls/:callId" element={<FundingCallPage />} />
              <Route path="funding/payments/:contributionId" element={<ContributionPage />} />
              <Route path="funding/debts/:debtId" element={<DebtPage />} />
              <Route path="funding/founders/:founderProfileId" element={<FounderFinancePage />} />
              <Route path="distributions" element={<DistributionsOverviewPage />} />
              <Route path="distributions/new" element={<NewDistributionPage />} />
              <Route path="distributions/scenario" element={<ScenarioPage />} />
              <Route path="distributions/reserve" element={<ReservePage />} />
              <Route path="distributions/:distributionId" element={<DistributionPage />} />
              <Route path="valuation" element={<ValuationOverviewPage />} />
              <Route path="valuation/new" element={<NewValuationPage />} />
              <Route path="valuation/comparables" element={<ComparablesPage />} />
              <Route path="valuation/:snapshotId" element={<ValuationSnapshotPage />} />
              <Route
                path="valuation/:snapshotId/recalculate"
                element={<RecalculateValuationPage />}
              />
            </Route>
            <Route path="search" element={<SearchPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="notifications/settings" element={<NotificationSettingsPage />} />
            <Route path="admin" element={<AdminLayout />}>
              <Route index element={<AdminIndex />} />
              <Route
                path="members"
                element={
                  <AdminSection path="members">
                    <MembersAdmin />
                  </AdminSection>
                }
              />
              <Route
                path="invitations"
                element={
                  <AdminSection path="invitations">
                    <InvitationsAdmin />
                  </AdminSection>
                }
              />
              <Route
                path="permissions"
                element={
                  <AdminSection path="permissions">
                    <PermissionGrantsAdmin />
                  </AdminSection>
                }
              />
              <Route
                path="founders"
                element={
                  <AdminSection path="founders">
                    <FoundersAdmin />
                  </AdminSection>
                }
              />
              <Route
                path="equity"
                element={
                  <AdminSection path="equity">
                    <CapTableAdmin />
                  </AdminSection>
                }
              />
              <Route
                path="governance"
                element={
                  <AdminSection path="governance">
                    <GovernanceAdmin />
                  </AdminSection>
                }
              />
              <Route
                path="finance"
                element={
                  <AdminSection path="finance">
                    <FinanceAdmin />
                  </AdminSection>
                }
              />
              <Route
                path="audit"
                element={
                  <AdminSection path="audit">
                    <AuditLogAdmin />
                  </AdminSection>
                }
              />
              <Route
                path="ai"
                element={
                  <AdminSection path="ai">
                    <AiActivityAdmin />
                  </AdminSection>
                }
              />
              <Route
                path="execution"
                element={
                  <AdminSection path="execution">
                    <ExecutionSettingsAdmin />
                  </AdminSection>
                }
              />
              <Route
                path="notifications"
                element={
                  <AdminSection path="notifications">
                    <NotificationPolicyAdmin />
                  </AdminSection>
                }
              />
              <Route
                path="security"
                element={
                  <AdminSection path="security">
                    <SecuritySettingsAdmin />
                  </AdminSection>
                }
              />
              <Route
                path="system"
                element={
                  <AdminSection path="system" bare>
                    <SystemStatus />
                  </AdminSection>
                }
              />
            </Route>
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </ReauthProvider>
  );
}

function NotFound() {
  return (
    <section className="stack">
      <h1>{phrase('Page not found')}</h1>
      <p>{phrase('The page you are looking for does not exist or has moved.')}</p>
      <Link to="/">{phrase('Go to your home page')}</Link>
    </section>
  );
}
